import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export default function CustomDatePicker({ value, min, max, onChange, className }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const parseDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const currentDate = parseDate(value);
  const minDate = min ? parseDate(min) : null;
  const maxDate = max ? parseDate(max) : null;
  
  const canNavigate = (direction) => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    
    if (minDate && newDate < minDate) return false;
    if (maxDate && newDate > maxDate) return false;
    return true;
  };
  
  const handleNavigate = (direction) => {
    if (!canNavigate(direction)) return;
    
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    
    onChange(formatDate(newDate));
  };
  
  const handleToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (minDate && today < minDate) return;
    if (maxDate && today > maxDate) return;
    onChange(formatDate(today));
  };
  
  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleNavigate('prev')}
          disabled={!canNavigate('prev')}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#08513F] ${className}`}
        >
          <Calendar className="w-4 h-4 text-[#6B7280]" />
          <span>{currentDate.toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
          })}</span>
        </button>
        
        <button
          type="button"
          onClick={() => handleNavigate('next')}
          disabled={!canNavigate('next')}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        
        {value !== formatDate(new Date()) && (
          <button
            type="button"
            onClick={handleToday}
            className="text-xs text-[#08513F] hover:underline"
          >
            Today
          </button>
        )}
      </div>
      
      {isOpen && (
        <div className="absolute top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg p-4 z-50">
          <div className="grid grid-cols-7 gap-1 text-center text-xs">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="font-semibold text-[#6B7280] p-1">
                {day}
              </div>
            ))}
          </div>
          {/* Calendar grid implementation would go here */}
          <div className="mt-2 text-center text-sm text-[#6B7280]">
            Use arrows to navigate dates
          </div>
        </div>
      )}
    </div>
  );
}
